// A board-sized RGBA picture the painter draws into, pixel by pixel, as `tools/encounter-art.py preview`
// draws its boards with Pillow (docs/186 EA2): the joins between pieces are made of the pieces' own pixels.
import type { Piece } from '../types';

export type RGBA = readonly [number, number, number, number];

/** How a piece is laid: mirrored, turned a quarter (clockwise), or both (docs/164 ET14's organic turns). */
export interface Turn { flipX?: boolean; flipY?: boolean; rot90?: boolean }

export class Canvas {
  readonly px: Uint8ClampedArray;
  constructor(readonly w: number, readonly h: number, fill?: RGBA) {
    this.px = new Uint8ClampedArray(w * h * 4);
    if (fill) {
      // one 32-bit word a pixel, in the platform's byte order
      const word = new Uint8ClampedArray(fill);
      new Uint32Array(this.px.buffer).fill(new Uint32Array(word.buffer)[0]!);
    }
  }

  inside(x: number, y: number): boolean { return x >= 0 && y >= 0 && x < this.w && y < this.h; }

  get(x: number, y: number): RGBA {
    const i = (y * this.w + x) * 4, p = this.px;
    return [p[i]!, p[i + 1]!, p[i + 2]!, p[i + 3]!];
  }

  /** Set a pixel outright (no blending), when it is on the canvas. */
  set(x: number, y: number, c: RGBA): void {
    if (!this.inside(x, y)) return;
    this.px.set(c, (y * this.w + x) * 4);
  }

  /** Lay `c` over the pixel by its alpha (Pillow's `alpha_composite`). */
  over(x: number, y: number, c: RGBA): void {
    if (!this.inside(x, y) || c[3] === 0) return;
    const i = (y * this.w + x) * 4, p = this.px;
    if (c[3] === 255) { p[i] = c[0]; p[i + 1] = c[1]; p[i + 2] = c[2]; p[i + 3] = 255; return; }
    const a = c[3] / 255, b = p[i + 3]! / 255, o = a + b * (1 - a);
    if (o <= 0) return;
    for (let k = 0; k < 3; k++) p[i + k] = (c[k]! * a + p[i + k]! * b * (1 - a)) / o;
    p[i + 3] = o * 255;
  }

  /** A piece's pixel at (u, v) of its own drawing, as laid with `turn` (the turned image is the same size: pieces that turn are square). */
  static sample(piece: Piece, u: number, v: number, turn?: Turn): RGBA {
    let x = u, y = v;
    if (turn?.rot90) { const t = x; x = y; y = piece.h - 1 - t; }
    if (turn?.flipX) x = piece.w - 1 - x;
    if (turn?.flipY) y = piece.h - 1 - y;
    const i = (y * piece.w + x) * 4, p = piece.px;
    return [p[i]!, p[i + 1]!, p[i + 2]!, p[i + 3]!];
  }

  /** Index into a piece's pixels of its drawing's (u, v) as laid with `turn`. */
  static index(piece: Piece, u: number, v: number, turn?: Turn): number {
    let x = u, y = v;
    if (turn) {
      if (turn.rot90) { const t = x; x = y; y = piece.h - 1 - t; }
      if (turn.flipX) x = piece.w - 1 - x;
      if (turn.flipY) y = piece.h - 1 - y;
    }
    return (y * piece.w + x) * 4;
  }

  /** Copy a piece's pixel (as laid with `turn`) onto (x, y) outright. */
  copy(x: number, y: number, piece: Piece, u: number, v: number, turn?: Turn): void {
    if (!this.inside(x, y)) return;
    const i = Canvas.index(piece, u, v, turn), o = (y * this.w + x) * 4, s = piece.px, p = this.px;
    p[o] = s[i]!; p[o + 1] = s[i + 1]!; p[o + 2] = s[i + 2]!; p[o + 3] = s[i + 3]!;
  }

  /** Lay a whole piece with its top left at (dx, dy). `replace` copies its pixels outright, transparent ones too. */
  draw(piece: Piece, dx: number, dy: number, turn?: Turn, replace = false): void {
    const s = piece.px, p = this.px;
    for (let v = 0; v < piece.h; v++) {
      const y = dy + v;
      if (y < 0 || y >= this.h) continue;
      for (let u = 0; u < piece.w; u++) {
        const x = dx + u;
        if (x < 0 || x >= this.w) continue;
        const i = turn ? Canvas.index(piece, u, v, turn) : (v * piece.w + u) * 4, o = (y * this.w + x) * 4;
        const a = s[i + 3]!;
        if (replace || a === 255) { p[o] = s[i]!; p[o + 1] = s[i + 1]!; p[o + 2] = s[i + 2]!; p[o + 3] = a; continue; }
        if (a === 0) continue;
        this.over(x, y, [s[i]!, s[i + 1]!, s[i + 2]!, a]);
      }
    }
  }

  /** Lay another canvas over this one with its top left at (dx, dy). */
  drawCanvas(src: Canvas, dx: number, dy: number): void {
    for (let v = 0; v < src.h; v++) {
      const y = dy + v;
      if (y < 0 || y >= this.h) continue;
      for (let u = 0; u < src.w; u++) {
        const i = (v * src.w + u) * 4;
        const a = src.px[i + 3]!;
        if (a === 0) continue;
        const x = dx + u;
        if (a === 255 && x >= 0 && x < this.w) { const o = (y * this.w + x) * 4; this.px[o] = src.px[i]!; this.px[o + 1] = src.px[i + 1]!; this.px[o + 2] = src.px[i + 2]!; this.px[o + 3] = 255; continue; }
        this.over(x, y, [src.px[i]!, src.px[i + 1]!, src.px[i + 2]!, a]);
      }
    }
  }

  /** A soft dark ellipse (a contact shadow), as Pillow's `ellipse((x0, y0, x1, y1))` fills it. */
  ellipse(x0: number, y0: number, x1: number, y1: number, c: RGBA): void {
    const cx = (x0 + x1) / 2, cy = (y0 + y1) / 2, rx = (x1 - x0) / 2 + 0.5, ry = (y1 - y0) / 2 + 0.5;
    for (let y = Math.floor(y0); y <= y1; y++) for (let x = Math.floor(x0); x <= x1; x++) {
      const dx = (x + 0.5 - (cx + 0.5)) / rx, dy = (y + 0.5 - (cy + 0.5)) / ry;
      if (dx * dx + dy * dy <= 1) this.over(x, y, c);
    }
  }

  /** A filled rectangle, inclusive of both corners (Pillow's `rectangle`). */
  rect(x0: number, y0: number, x1: number, y1: number, c: RGBA): void {
    for (let y = y0; y <= y1; y++) for (let x = x0; x <= x1; x++) this.over(x, y, c);
  }

  /** A copy of the rectangle as a new canvas (for cutting a raised wall row into squares). */
  crop(x0: number, y0: number, w: number, h: number): Canvas {
    const out = new Canvas(w, h);
    for (let v = 0; v < h; v++) {
      const y = y0 + v;
      if (y < 0 || y >= this.h) continue;
      const a = Math.max(0, x0), b = Math.min(this.w, x0 + w);
      if (b > a) out.px.set(this.px.subarray((y * this.w + a) * 4, (y * this.w + b) * 4), (v * w + (a - x0)) * 4);
    }
    return out;
  }

  /** Whether any pixel is drawn. */
  empty(): boolean { for (let i = 3; i < this.px.length; i += 4) if (this.px[i]) return false; return true; }
}

/** `#rrggbb` → RGBA. */
export function hex(h: string, a = 255): RGBA {
  const s = h.replace('#', '');
  return [parseInt(s.slice(0, 2), 16), parseInt(s.slice(2, 4), 16), parseInt(s.slice(4, 6), 16), a];
}

export const sameRGB = (a: RGBA, b: RGBA): boolean => a[0] === b[0] && a[1] === b[1] && a[2] === b[2];
